import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight, PaperPlaneTilt, Package, Clock, XCircle, Prohibit, Check, LinkSimple, PencilSimple, CurrencyDollar, Scales, MagnifyingGlass, CaretDoubleDown } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalCard, BrutalButton } from "../components/Brutal";
import CancelSaleDialog from "../components/CancelSaleDialog";
import MarkSamplePaidDialog from "../components/MarkSamplePaidDialog";
import EditMovementDialog from "../components/EditMovementDialog";
import { useAuth } from "../AuthContext";

const PAGE_SIZE = 500;

export default function MovementLogPage() {
  const { t, i18n } = useTranslation();
  const { user, isSuperOwner, isElevated } = useAuth();
  const [items, setItems] = useState([]);   // live first page (auto-refreshed)
  const [older, setOlder] = useState([]);   // v414.8 — appended "load older" pages
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");     // input value
  const [query, setQuery] = useState("");       // debounced, sent to the server
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  // Default OFF — hide cancelled sale rows AND their audit cancel pair.
  const [showCancellations, setShowCancellations] = useState(false);

  // Owner / admin / cfo can cancel sales. Super-owner inherits via owner role.
  const canCancelSales =
    !!user &&
    user !== false &&
    ["owner", "admin", "cfo"].includes(user.role);
  // v403.4 — log editing is owner/admin ONLY (strictest, per owner directive).
  // v406.2 — allowlisted super-owners (Harvey/Sukhinder) get god-mode parity.
  const canEditLogs = !!user && user !== false && (["owner", "admin"].includes(user.role) || isSuperOwner);
  // Helper: can this user retroactively mark a given sample row as paid?
  //   - owner / admin / cfo: any row in their org root.
  //   - distributor / others: only rows they originated.
  const canMarkSamplePaid = (row) => {
    if (!user || user === false) return false;
    if (row.cancelled || row.paid) return false;
    if (row.transfer_type !== "sample") return false;
    if (["owner", "admin", "cfo"].includes(user.role)) return true;
    return row.from_user_id === user.id;
  };
  // Same RBAC for the inverse action (unpay) — except checks paid=true.
  const canUnpaySample = (row) => {
    if (!user || user === false) return false;
    if (row.cancelled || !row.paid) return false;
    if (row.transfer_type !== "sample") return false;
    if (["owner", "admin", "cfo"].includes(user.role)) return true;
    return row.from_user_id === user.id;
  };
  const unmarkPaid = async (m) => {
    if (!window.confirm(t("mark_sample_paid.confirm_unpay", { product: m.product_name }))) return;
    try {
      await api.post(`/movement-log/${m.id}/unpay`);
      toast.success(
        m.owed
          ? t("mark_sample_paid.toast_unpaid_owed", { product: m.product_name })
          : t("mark_sample_paid.toast_unpaid", { product: m.product_name })
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = async (q = query) => {
    try {
      const params = { limit: PAGE_SIZE };
      if (q) params.q = q;
      const { data } = await api.get("/movement-log", { params });
      setItems(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // v414.8 — page back beyond the first batch (full history, server-side).
  const loadOlder = async () => {
    const all = [...items, ...older];
    const oldest = all[all.length - 1]?.created_at;
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const params = { limit: PAGE_SIZE, before: oldest };
      if (query) params.q = query;
      const { data } = await api.get("/movement-log", { params });
      setOlder((o) => [...o, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  // Debounce the search box → server query over the FULL history.
  useEffect(() => {
    const tm = setTimeout(() => {
      setQuery(search.trim());
    }, 400);
    return () => clearTimeout(tm);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    setOlder([]);
    load(query);
    // Auto-refresh only refreshes the live first page; paged-in history
    // stays put (append-only), so refreshes never clobber your scrollback.
    const tm = setInterval(() => load(query), 10000);
    return () => clearInterval(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fmtWhen = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(i18n.language === "ru" ? "ru-RU" : "en-US", {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  // Group rows by calendar month (e.g. "August 2026") for a scannable log.
  const monthLabel = (iso) => {
    const d = new Date(iso);
    const s = d.toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", {
      month: "long", year: "numeric",
    });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const groupByMonth = (rows) => {
    const map = new Map();
    rows.forEach((m) => {
      const d = new Date(m.created_at);
      const key = Number.isNaN(d.getTime())
        ? "unknown"
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, { key, label: monthLabel(m.created_at), rows: [] });
      map.get(key).rows.push(m);
    });
    return [...map.values()];
  };

  return (
    <div className="space-y-6" data-testid="movement-log-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("movement.title")}</h1>
          <p className="text-sm sm:text-base text-zinc-600">
            {isSuperOwner ? t("movement.subtitle_super") : t("movement.subtitle_user")}
          </p>
        </div>
        <label
          className="inline-flex items-center gap-2 text-sm select-none cursor-pointer"
          data-testid="show-cancellations-toggle-label"
        >
          <input
            type="checkbox"
            checked={showCancellations}
            onChange={(e) => setShowCancellations(e.target.checked)}
            data-testid="show-cancellations-toggle"
            className="h-4 w-4 accent-rose-600"
          />
          <span className="text-zinc-700">{t("movement.show_cancellations")}</span>
        </label>
        {canCancelSales && (
          <Link
            to="/reconciliation"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a] rounded-lg px-3 py-2 hover:bg-[#f5ecd2]"
            data-testid="open-reconciliation-link"
          >
            <Scales size={15} weight="bold" /> {t("recon.open_btn")}
          </Link>
        )}
      </div>

      {/* v414.8 — full-history search (product, person, note) */}
      <div className="relative" data-testid="movement-search-wrap">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("movement.search_ph")}
          className="w-full h-11 pl-9 pr-3 rounded-xl border border-zinc-300 bg-white text-sm font-medium focus:outline-none focus:border-[#C9A84C]"
          data-testid="movement-search-input"
        />
        {query && !loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-[#8a6a1c]" data-testid="movement-search-count">
            {t("movement.search_matches", { n: [...items, ...older].length })}
          </span>
        )}
      </div>

      {(() => {
        const seen = new Set();
        const all = [...items, ...older].filter((m) => {
          if (seen.has(m.id)) return false;
          seen.add(m.id);
          return true;
        });
        const visible = showCancellations
          ? all
          : all.filter(
              (m) => !m.cancelled && m.kind !== "cancel" && m.transfer_type !== "sale_cancelled"
            );
        if (loading) {
          return <p className="text-sm text-zinc-500">{t("common.loading")}</p>;
        }
        if (visible.length === 0) {
          return (
            <BrutalCard>
              <div className="flex items-start gap-3 text-zinc-600">
                <Package size={22} weight="bold" className="shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed" data-testid="movement-empty-text">
                  {items.length === 0
                    ? t("movement.empty")
                    : t("movement.empty_filtered")}
                </p>
              </div>
            </BrutalCard>
          );
        }
        return (
          <div className="space-y-6" data-testid="movement-rows">
            {groupByMonth(visible).map((g) => (
              <div key={g.key} className="space-y-2" data-testid={`movement-month-group-${g.key}`}>
                <div className="flex items-center gap-3 pt-1">
                  <h2
                    className="text-sm font-bold uppercase tracking-widest text-zinc-900"
                    data-testid={`movement-month-label-${g.key}`}
                  >
                    {g.label}
                  </h2>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#fbf6e7] border border-[#c9a24a] text-[#8a6a1c] tabular-nums">
                    {g.rows.length}
                  </span>
                  <div className="flex-1 h-px bg-zinc-200" />
                </div>
                {g.rows.map((m) => (
                  isElevated ? (
                    <MovementRow
                      key={m.id}
                      item={m}
                      fmtWhen={fmtWhen}
                      canCancelSales={canCancelSales}
                      canEdit={canEditLogs && m.kind === "assign" && !m.cancelled}
                      canMarkPaid={canMarkSamplePaid(m)}
                      canUnpay={canUnpaySample(m)}
                      onCancelClick={() => setCancelTarget(m)}
                      onEditClick={() => setEditTarget(m)}
                      onMarkPaidClick={() => setMarkPaidTarget(m)}
                      onUnpayClick={() => unmarkPaid(m)}
                    />
                  ) : (
                    <SimpleMovementRow key={m.id} item={m} fmtWhen={fmtWhen} uid={user?.id} />
                  )
                ))}
              </div>
            ))}
            {hasMore && (
              <div className="pt-1 pb-2 text-center">
                <BrutalButton
                  variant="secondary"
                  disabled={loadingMore}
                  onClick={loadOlder}
                  data-testid="movement-load-older-btn"
                >
                  <CaretDoubleDown size={14} weight="bold" />{" "}
                  {loadingMore ? t("movement.loading_older") : t("movement.load_older")}
                </BrutalButton>
              </div>
            )}
          </div>
        );
      })()}

      {cancelTarget && (
        <CancelSaleDialog
          sale={{
            id: cancelTarget.id,
            product_name: cancelTarget.product_name,
            quantity: cancelTarget.quantity,
            unit: cancelTarget.unit,
            sale_price: cancelTarget.sale_price,
          }}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => load()}
        />
      )}
      {markPaidTarget && (
        <MarkSamplePaidDialog
          row={markPaidTarget}
          onClose={() => setMarkPaidTarget(null)}
          onPaid={() => load()}
        />
      )}
      {editTarget && (
        <EditMovementDialog
          row={{
            id: editTarget.id,
            quantity: editTarget.quantity,
            sale_price: editTarget.sale_price,
            customer_name: editTarget.to_user_name || editTarget.recipient_name || "",
            date: editTarget.created_at,
            note: editTarget.note || "",
            qtyEditable: editTarget.kind === "assign" && ["sale", "sample"].includes(editTarget.transfer_type),
          }}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function MovementRow({ item, fmtWhen, canCancelSales, canEdit, canMarkPaid, canUnpay, onCancelClick, onEditClick, onMarkPaidClick, onUnpayClick }) {
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  // v403.4 account-charge money rows (kind=settlement, qty 0)
  const isCharge = ["account_charge", "account_charge_voided", "payout_deduction"].includes(item.transfer_type);
  const kindLabel = isCharge
    ? t(`charges.log_${item.transfer_type}`)
    : ({
        assign: t("movement.kind_assign"),
        intake: t("movement.kind_intake"),
        fulfill: t("movement.kind_fulfill"),
        cancel: t("movement.kind_cancel"),
        production: t("movement.kind_production"),
        conversion: t("movement.kind_conversion"),
        settlement: t("movement.kind_settlement"),
      })[item.kind] || item.kind;

  const isSettlement = item.kind === "settlement";

  const isSale = item.transfer_type === "sale" && item.kind === "assign";
  const isSample = item.transfer_type === "sample" && item.kind === "assign";
  const isCancellation = item.kind === "cancel";
  const isCancelled = !!item.cancelled;
  const isPaid = !!item.paid;
  const isOwed = !!item.owed && !isPaid;
  const showCancelBtn = canCancelSales && isSale && !isCancelled;
  const showMarkPaidBtn = !!canMarkPaid && isSample && !isPaid && !isCancelled;
  const showUnpayBtn = !!canUnpay && isSample && isPaid && !isCancelled;

  return (
    <div
      className={`bg-white border rounded-xl p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 ${
        isCancelled ? "border-rose-200 bg-rose-50/30" : isCancellation ? "border-amber-200" : "border-zinc-200"
      }`}
      data-testid={`movement-row-${item.id}`}
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0 min-w-0 max-w-full">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          isCancellation ? "bg-amber-50 border border-amber-200 text-amber-700"
          : isCharge ? "bg-rose-50 border border-rose-200 text-rose-700"
          : "bg-[#fbf6e7] border border-[#c9a24a] text-[#8a6a1c]"
        }`}>
          {isCancellation ? <Prohibit size={16} weight="bold" /> : isCharge ? <CurrencyDollar size={16} weight="bold" /> : <PaperPlaneTilt size={16} weight="bold" />}
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${
          isCancellation ? "bg-amber-50 border-amber-200 text-amber-800"
          : isCharge ? "bg-rose-50 border-rose-200 text-rose-800"
          : "bg-zinc-100 border-zinc-200 text-zinc-700"
        }`}>
          {kindLabel}
        </span>
        {item.chain_id && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-950 border border-zinc-950 text-[#e4cf94] text-[10px] font-semibold uppercase tracking-wider"
            title={t("movement.chain_tooltip")}
            data-testid={`movement-chain-badge-${item.id}`}
          >
            <LinkSimple size={11} weight="bold" /> {t("movement.chain_badge", { hop: item.chain_hop || 1 })}
          </span>
        )}
        {isCancelled && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-rose-600 border-rose-700 text-white text-[10px] font-bold tracking-wider uppercase"
            data-testid={`movement-void-badge-${item.id}`}
          >
            <XCircle size={11} weight="bold" /> {t("cancel_sale.void_badge")}
          </span>
        )}
        {isPaid && isSample && !isCancelled && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider"
            data-testid={`movement-paid-badge-${item.id}`}
          >
            <Check size={11} weight="bold" /> {t("mark_sample_paid.paid_badge")}
          </span>
        )}
        {isOwed && isSample && !isCancelled && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 border border-amber-600 text-white text-[10px] font-bold uppercase tracking-wider"
            data-testid={`movement-owed-badge-${item.id}`}
          >
            <Clock size={11} weight="bold" /> {t("movement.owed_badge")}
            {item.owed_amount != null || item.sale_price != null ? (
              <span className="tabular-nums">
                ${Number(item.owed_amount != null ? item.owed_amount : item.sale_price * (item.quantity || 1)).toFixed(2)}
              </span>
            ) : null}
          </span>
        )}
        {item.edited && (
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[10px] font-bold tracking-wider uppercase hover:bg-amber-100"
            title={t("edit_log.edited_by", { who: item.edited_by_name || item.edited_by_email || "", when: fmtWhen(item.edited_at) })}
            data-testid={`movement-edited-badge-${item.id}`}
          >
            <PencilSimple size={11} weight="bold" /> {t("edit_log.edited_badge")}
          </button>
        )}
      </div>

      <div className={`flex-1 min-w-0 ${isCancelled ? "line-through text-zinc-400" : ""}`}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className={`font-semibold truncate ${isCancelled ? "text-zinc-400" : "text-zinc-900"}`} data-testid={`movement-from-${item.id}`}>
            {item.from_user_name || item.from_user_email}
          </span>
          <ArrowRight size={14} weight="bold" className={`shrink-0 ${isCancelled ? "text-zinc-300" : "text-zinc-400"}`} />
          <span className={`font-semibold truncate ${isCancelled ? "text-zinc-400" : "text-zinc-900"}`} data-testid={`movement-to-${item.id}`}>
            {item.to_user_name || item.to_user_email}
          </span>
        </div>
        <div className={`mt-1 text-sm ${isCancelled ? "text-zinc-400" : "text-zinc-600"}`}>
          {isSettlement ? (
            <span className={`font-semibold ${isCharge ? "text-rose-700" : "text-emerald-700"}`} data-testid={`movement-settlement-amount-${item.id}`}>
              {isCharge && item.transfer_type !== "account_charge_voided" ? "−" : ""}${Number(item.settlement_amount || 0).toFixed(2)}
            </span>
          ) : (
            <>
              <span className={`font-semibold ${isCancelled ? "text-zinc-400" : "text-zinc-900"}`}>{item.quantity} {item.unit || "units"}</span>
              {" "}·{" "}
              <span className="font-medium">{item.product_name}</span>
            </>
          )}
          {(isSale || isSample) && item.sale_price != null ? (
            <span
              className={`ml-2 text-xs font-semibold ${isCancelled ? "text-zinc-400" : isOwed ? "text-amber-700" : "text-emerald-700"}`}
              data-testid={`movement-sale-price-${item.id}`}
            >
              {isOwed
                ? t("movement.owed_at", { price: Number(item.sale_price).toFixed(2) })
                : t("movement.sold_at", { price: Number(item.sale_price).toFixed(2) })}
            </span>
          ) : item.wholesale_price != null && (
            <span className={`ml-2 text-xs ${isCancelled ? "text-zinc-400" : "text-zinc-500"}`}>
              {t("pricing.wholesale_short")} ${Number(item.wholesale_price).toFixed(2)}
            </span>
          )}
        </div>
        {item.note && (
          <div
            className={`mt-1 text-xs italic ${isCancelled ? "text-zinc-400" : "text-zinc-500"}`}
            data-testid={`movement-note-${item.id}`}
          >
            &ldquo;{item.note}&rdquo;
          </div>
        )}
        {item.edited && showOriginal && item.original_values && (
          <div
            className="mt-1.5 text-[11px] bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 text-amber-900"
            data-testid={`movement-original-values-${item.id}`}
          >
            <span className="font-semibold uppercase tracking-wide">{t("edit_log.original_label")}:</span>{" "}
            {t("edit_log.original_qty")} {item.original_values.quantity ?? "—"}
            {" · "}{t("edit_log.original_price")} {item.original_values.sale_price != null ? `$${Number(item.original_values.sale_price).toFixed(2)}` : "—"}
            {" · "}{t("edit_log.original_customer")} {item.original_values.to_user_name || item.original_values.recipient_name || "—"}
            {" · "}{t("edit_log.original_date")} {item.original_values.created_at ? fmtWhen(item.original_values.created_at) : "—"}
            {item.original_values.note ? <> · {t("edit_log.original_note")} &ldquo;{item.original_values.note}&rdquo;</> : null}
            <div className="mt-0.5 text-amber-700">
              {t("edit_log.edited_by", { who: item.edited_by_name || item.edited_by_email || "", when: fmtWhen(item.edited_at) })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 shrink-0 min-w-0 max-w-full">
        <div className="flex items-center gap-1.5 text-xs text-zinc-500">
          <Clock size={12} weight="bold" />
          <span>{fmtWhen(item.created_at)}</span>
        </div>
        {item.performed_by_name && (
          <span
            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-600"
            title={item.performed_by_email || ""}
            data-testid={`performed-by-${item.id}`}
          >
            {t("movement.performed_by", { name: item.performed_by_name })}
          </span>
        )}
        {canEdit && (
          <BrutalButton
            size="sm"
            variant="secondary"
            onClick={onEditClick}
            data-testid={`edit-log-btn-${item.id}`}
            title={t("edit_log.button_tooltip")}
            className="!px-2"
          >
            <PencilSimple size={14} weight="bold" />
          </BrutalButton>
        )}
        {showCancelBtn && (
          <BrutalButton
            size="sm"
            variant="secondary"
            onClick={onCancelClick}
            data-testid={`cancel-sale-btn-${item.id}`}
            className="!border-rose-300 !text-rose-700 hover:!bg-rose-50"
          >
            <XCircle size={14} weight="bold" /> {t("cancel_sale.button_short")}
          </BrutalButton>
        )}
        {showMarkPaidBtn && (
          <BrutalButton
            size="sm"
            variant="secondary"
            onClick={onMarkPaidClick}
            data-testid={`mark-paid-btn-${item.id}`}
            className="!border-emerald-300 !text-emerald-700 hover:!bg-emerald-50"
          >
            <Check size={14} weight="bold" /> {t("mark_sample_paid.button_short")}
          </BrutalButton>
        )}
        {showUnpayBtn && (
          <BrutalButton
            size="sm"
            variant="secondary"
            onClick={onUnpayClick}
            data-testid={`unpay-btn-${item.id}`}
            className="!border-zinc-300 !text-zinc-600 hover:!bg-zinc-50"
            title={t("mark_sample_paid.unpay_tooltip")}
          >
            {t("mark_sample_paid.unpay_short")}
          </BrutalButton>
        )}
      </div>
    </div>
  );
}

// v405.3 — simplified movement row for DISTRIBUTORS. No chain badges, no
// admin attribution, no internal kind labels — just what happened, to whom,
// when. Elevated users keep the full MovementRow above.
function SimpleMovementRow({ item, fmtWhen, uid }) {
  const { t } = useTranslation();
  const classify = () => {
    if (item.kind === "adjust" || item.action === "adjust") return "act_adjusted";
    if (item.kind === "return" || item.transfer_type === "return" || item.action === "return") return "act_returned";
    if (item.to_user_id === uid) return "act_received";
    if (item.from_user_id === uid) return item.transfer_type === "sale" ? "act_sold" : "act_gave";
    return "act_adjusted";
  };
  const key = classify();
  const BADGE = {
    act_received: "bg-[#fbf6e7] text-[#8a6a1c] border-[#c9a24a]/60",
    act_sold: "bg-emerald-50 text-emerald-800 border-emerald-300",
    act_gave: "bg-zinc-50 text-zinc-700 border-zinc-300",
    act_returned: "bg-blue-50 text-blue-800 border-blue-300",
    act_adjusted: "bg-zinc-50 text-zinc-700 border-zinc-300",
  };
  const counterparty =
    key === "act_received"
      ? (item.from_user_name ? t("movement.simple_from", { name: item.from_user_name }) : "")
      : (item.to_user_name || item.recipient_name
          ? t("movement.simple_to", { name: item.to_user_name || item.recipient_name })
          : "");
  return (
    <div
      className={`bg-white border border-zinc-200 rounded-lg p-3 flex flex-wrap items-center gap-x-3 gap-y-1 ${item.cancelled ? "opacity-50" : ""}`}
      data-testid={`simple-movement-row-${item.id}`}
    >
      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${BADGE[key]}`}>
        {t(`dist_portal.${key}`)}
      </span>
      <span className={`text-sm font-semibold text-zinc-900 min-w-0 truncate ${item.cancelled ? "line-through" : ""}`}>
        {item.product_name || "—"}
      </span>
      <span className="text-sm font-semibold tabular-nums shrink-0">
        ×{Math.abs(Number(item.quantity) || 0)}
      </span>
      {key === "act_sold" && item.sale_price != null && (
        <span className="text-sm font-semibold text-emerald-700 tabular-nums shrink-0">
          ${Number(item.sale_price).toFixed(2)}
        </span>
      )}
      {counterparty && <span className="text-xs text-zinc-500 min-w-0 truncate">{counterparty}</span>}
      <span className="text-[11px] text-zinc-400 tabular-nums ml-auto shrink-0">{fmtWhen(item.created_at)}</span>
    </div>
  );
}
